import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, ArrowLeftRight } from "lucide-react";
import { api } from "@/lib/api";
import { useShop } from "@/context/ShopContext";
import { formatPrice } from "@/lib/constants";
import Reveal from "@/components/Reveal";
import StarRating from "@/components/StarRating";

const Compare = () => {
  const [perfumes, setPerfumes] = useState([]);
  const { compare, toggleCompare } = useShop();

  useEffect(() => {
    api.get("/perfumes").then((r) => setPerfumes(r.data)).catch(() => {});
  }, []);

  const selected = perfumes.filter((p) => compare.includes(p.id));

  const rows = [
    { label: "Prix 30 ml", render: (p) => formatPrice(p.prices["30"]) },
    { label: "Prix 50 ml", render: (p) => formatPrice(p.prices["50"]) },
    { label: "Prix 100 ml", render: (p) => formatPrice(p.prices["100"]) },
    { label: "Famille olfactive", render: (p) => p.family },
    { label: "Genre", render: (p) => p.gender },
    { label: "Notes de tête", render: (p) => (p.notes?.tete || []).join(", ") },
    { label: "Notes de cœur", render: (p) => (p.notes?.coeur || []).join(", ") },
    { label: "Notes de fond", render: (p) => (p.notes?.fond || []).join(", ") },
    { label: "Occasion", render: (p) => (p.occasions || []).join(", ") },
    { label: "Note clients", render: (p) => (p.rating ? `${p.rating}/5 (${p.review_count} avis)` : "Pas encore noté") },
    { label: "Disponibilité", render: (p) => (p.in_stock ? "En stock" : "Rupture") },
  ];

  return (
    <div className="mx-auto max-w-7xl px-4 py-12 sm:px-8">
      <Reveal>
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#C59B27]">Comparateur</p>
        <h1 className="mt-2 font-serif text-3xl text-[#1A1714] sm:text-4xl">Comparer les parfums</h1>
        <p className="mt-3 text-sm text-[#5C5449]">
          Sélectionnez jusqu'à 3 parfums (icône <ArrowLeftRight className="inline" size={14} /> sur les fiches) pour les comparer côte à côte.
        </p>
      </Reveal>

      {selected.length === 0 ? (
        <div className="py-20 text-center" data-testid="compare-empty">
          <p className="font-serif text-2xl text-[#1A1714]">Aucun parfum sélectionné</p>
          <Link to="/boutique" className="mt-6 inline-block rounded-full bg-[#C59B27] px-8 py-3.5 text-sm font-semibold text-white transition-all hover:bg-[#A8821B] active:scale-95">
            Choisir des parfums
          </Link>
        </div>
      ) : (
        <Reveal className="mt-10 overflow-x-auto rounded-3xl border border-[#EADFCB] bg-white">
          <table className="w-full min-w-[640px]" data-testid="compare-table">
            <thead>
              <tr className="border-b border-[#EADFCB]">
                <th className="p-4 text-left text-xs font-semibold uppercase tracking-[0.2em] text-[#C59B27]">Critère</th>
                {selected.map((p) => (
                  <th key={p.id} className="min-w-[200px] p-4 align-top">
                    <div className="relative">
                      <button
                        data-testid={`compare-remove-${p.id}`}
                        onClick={() => toggleCompare(p.id)}
                        className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full bg-[#1A1714] text-white hover:bg-red-500"
                        aria-label="Retirer"
                      >
                        <X size={12} />
                      </button>
                      <Link to={`/parfum/${p.id}`}>
                        <img src={p.image} alt={p.name} className="mx-auto h-36 w-28 rounded-xl object-cover" />
                        <p className="mt-3 text-xs font-semibold uppercase tracking-[0.15em] text-[#C59B27]">{p.brand}</p>
                        <p className="font-serif text-lg font-semibold text-[#1A1714]">{p.name}</p>
                      </Link>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => (
                <tr key={row.label} className={ri % 2 === 0 ? "bg-[#FAF8F5]" : "bg-white"}>
                  <td className="p-4 text-xs font-semibold uppercase tracking-[0.12em] text-[#5C5449]">{row.label}</td>
                  {selected.map((p) => (
                    <td key={p.id} className="p-4 text-center text-sm text-[#1A1714]">{row.render(p)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </Reveal>
      )}

      {selected.length > 0 && selected.length < 3 && (
        <div className="mt-10">
          <p className="text-sm font-semibold text-[#1A1714]">Ajouter un parfum à comparer :</p>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {perfumes
              .filter((p) => !compare.includes(p.id))
              .slice(0, 6)
              .map((p) => (
                <button
                  key={p.id}
                  data-testid={`compare-add-${p.id}`}
                  onClick={() => toggleCompare(p.id)}
                  className="rounded-xl border border-[#EADFCB] bg-white p-3 text-left transition-all hover:border-[#C59B27] active:scale-95"
                >
                  <img src={p.image} alt={p.name} className="h-20 w-full rounded-lg object-cover" />
                  <p className="mt-2 truncate text-xs font-semibold text-[#1A1714]">{p.name}</p>
                  <p className="text-xs text-[#C59B27]">{formatPrice(p.prices["50"])}</p>
                </button>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Compare;
